import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ArrowUpRight, CheckCircle2, Loader2, PhoneCall, FileText } from "lucide-react";
import { EASE } from "@/components/site/Reveal";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_STEPS = 7;
const CAL_URL = "https://cal.com/saber-bellaaj-5ogauj/rdv-weyb.fr";

const CERTS = {
    olive: ["EU Organic", "ISO 9001", "HACCP", "ISO 22000", "BRC Global", "IFS", "DOP/IGP/AOP", "Extra Virgin", "Halal", "Kosher", "Non-GMO", "Fairtrade"],
    textiles: ["GOTS", "Fair Trade", "SA8000", "BSCI", "Oeko-Tex 100", "WRAP", "ISO 9001", "ISO 14001", "ISO 45001", "Organic Cotton", "Recycled Fibers", "Halal", "REACH"],
    pam: ["EU Organic", "Biodynamic", "Pesticide-Free", "ISO 9001", "ISO 22000", "GMP", "GACP", "USP", "PDO/PGI", "Heavy Metals Test", "Pesticide Test", "Aflatoxin Test", "Fairtrade", "Rainforest Alliance"],
};

const COPY = {
    fr: {
        metaTitle: "Trouver Fournisseurs MENA | Sourcing Olive Oil, Textiles, PAM",
        metaDesc: "Connectez-vous avec des fournisseurs pré-vérifiés en Tunisie, Égypte, Maroc, Jordanie. Huile d'olive, textiles, plantes aromatiques. Devis gratuit en 24h.",
        heroTitle: "Sourcing MENA — Acheteurs & Fournisseurs",
        heroSubtitle: "Connectez-vous avec des partenaires pré-vérifiés en Tunisie, Égypte, Maroc et Jordanie. Réponse personnalisée sous 24h.",
        stepOf: "Étape {x} sur 7",
        steps: ["Qui êtes-vous ?", "Secteur & pays", "Vos besoins", "Certifications", "Décrivez votre besoin", "Vos coordonnées", "Finaliser votre demande"],
        buyer: "Je suis ACHETEUR (je cherche des fournisseurs)",
        supplier: "Je suis FOURNISSEUR (je cherche des acheteurs)",
        err1: "Veuillez sélectionner votre profil",
        sectorLabel: "Secteur d'activité",
        sectorPh: "Choisir un secteur",
        sectors: { olive: "🫒 Huile d'Olive & Produits Alimentaires", textiles: "👕 Textiles & Vêtements", pam: "🌿 Plantes Aromatiques & Médicinales" },
        countriesLabel: "Pays intéressés (multi-sélection)",
        countriesHelp: "Sélectionnez un ou plusieurs pays",
        countries: { TN: "🇹🇳 Tunisie", EG: "🇪🇬 Égypte", MA: "🇲🇦 Maroc", JO: "🇯🇴 Jordanie" },
        err2: "Sélectionnez un secteur et au moins 1 pays",
        budgetLabel: "Budget annuel prévu",
        budgetPh: "Choisir un budget",
        budgets: ["€10k - €50k", "€50k - €200k", "€200k - €1M", "Plus de €1M", "À définir"],
        timelineLabel: "Délai souhaité",
        timelinePh: "Choisir un délai",
        timelines: ["Urgent (< 2 semaines)", "Moyen terme (1-2 mois)", "Long terme (> 2 mois)", "Pas défini"],
        volumeLabel: "Volume de production mensuel",
        volumePh: "Choisir un volume",
        volumes: ["Petit (< 50 unités/tonnes)", "Moyen (50-500)", "Grand (500+)", "À définir"],
        err3: "Veuillez répondre aux champs requis",
        certLabel: "Certifications actuelles (multi-sélection)",
        certHelp: "Sélectionnez celles qui s'appliquent (optionnel)",
        descLabel: "Décrivez votre besoin",
        descPh: "Ex: Cherche 5-10 fournisseurs d'huile d'olive tunisienne, 50 tonnes/mois, certifiés bio EU, qualité premium...",
        err5: "Minimum 20 caractères requis",
        lName: "Nom complet", lCompany: "Entreprise", lEmail: "Email", lPhone: "Téléphone", lCountry: "Pays",
        err6: "Vérifiez vos informations (email valide, téléphone, etc.)",
        s7sub: "Choisissez comment vous souhaitez procéder",
        quoteTitle: "Demander un devis",
        quoteDesc: "Je vous enverrai une proposition détaillée par email dans 24h",
        callTitle: "Réserver un appel",
        callDesc: "Discutons directement de vos besoins (20-30 min)",
        sending: "Envoi…",
        back: "Précédent",
        next: "Suivant",
        successTitle: "Demande envoyée !",
        successText: "Vous recevrez un email de confirmation et une proposition sous 24h.",
        successToast: "Votre demande de sourcing a bien été envoyée.",
        backHome: "Retour à l'accueil",
        bookCall: "Réserver un appel",
        errGeneric: "Une erreur est survenue, réessayez.",
    },
    en: {
        metaTitle: "Find MENA Suppliers | Sourcing Olive Oil, Textiles, Medicinal Plants",
        metaDesc: "Connect with pre-vetted suppliers in Tunisia, Egypt, Morocco, Jordan. Olive oil, textiles, medicinal plants. Free quote within 24 hours.",
        heroTitle: "MENA Sourcing — Buyers & Suppliers",
        heroSubtitle: "Connect with pre-vetted partners in Tunisia, Egypt, Morocco and Jordan. Tailored response within 24 hours.",
        stepOf: "Step {x} of 7",
        steps: ["Who are you?", "Sector & countries", "Your requirements", "Certifications", "Describe your needs", "Your contact info", "Finalize your request"],
        buyer: "I'm a BUYER (looking for suppliers)",
        supplier: "I'm a SUPPLIER (looking for buyers)",
        err1: "Please select your profile",
        sectorLabel: "Sector",
        sectorPh: "Choose a sector",
        sectors: { olive: "🫒 Olive Oil & Food Products", textiles: "👕 Textiles & Apparel", pam: "🌿 Aromatic & Medicinal Plants" },
        countriesLabel: "Countries of interest (multi-select)",
        countriesHelp: "Select one or more countries",
        countries: { TN: "🇹🇳 Tunisia", EG: "🇪🇬 Egypt", MA: "🇲🇦 Morocco", JO: "🇯🇴 Jordan" },
        err2: "Select a sector and at least 1 country",
        budgetLabel: "Expected annual budget",
        budgetPh: "Choose a budget",
        budgets: ["€10k - €50k", "€50k - €200k", "€200k - €1M", "Above €1M", "To be defined"],
        timelineLabel: "Timeline",
        timelinePh: "Choose a timeline",
        timelines: ["Urgent (< 2 weeks)", "Standard (1-2 months)", "Flexible (> 2 months)", "Not defined"],
        volumeLabel: "Monthly production volume",
        volumePh: "Choose a volume",
        volumes: ["Small (< 50 units/tonnes)", "Medium (50-500)", "Large (500+)", "To be defined"],
        err3: "Please complete the required fields",
        certLabel: "Current certifications (multi-select)",
        certHelp: "Select those that apply (optional)",
        descLabel: "Describe your needs",
        descPh: "E.g.: Looking for 5-10 Tunisian olive oil suppliers, 50 tonnes/month, EU organic certified, premium quality...",
        err5: "Minimum 20 characters required",
        lName: "Full name", lCompany: "Company", lEmail: "Email", lPhone: "Phone", lCountry: "Country",
        err6: "Please check your info (valid email, phone, etc.)",
        s7sub: "Choose how you'd like to proceed",
        quoteTitle: "Request a quote",
        quoteDesc: "I'll send you a detailed proposal by email within 24h",
        callTitle: "Book a call",
        callDesc: "Let's discuss your needs directly (20-30 min)",
        sending: "Sending…",
        back: "Back",
        next: "Next",
        successTitle: "Request sent!",
        successText: "You'll receive a confirmation email and a proposal within 24h.",
        successToast: "Your sourcing request has been sent.",
        backHome: "Back to home",
        bookCall: "Book a call",
        errGeneric: "Something went wrong, please retry.",
    },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const initialForm = {
    userType: "", sector: "", countries: [], budget: "", timeline: "", volume: "",
    certifications: [], description: "", name: "", company: "", email: "", phone: "", country: "",
};

const labelCls = "block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 mb-2";
const inputCls = "w-full bg-white border border-gray-200 focus:border-weyb-yellow focus:ring-1 focus:ring-weyb-yellow outline-none px-4 py-3 rounded-sm text-sm text-weyb-ink placeholder:text-gray-400 transition-colors";
const selectCls = inputCls;

export default function Sourcing() {
    usePageMeta(
        { fr: "Sourcing MENA — WEYB", en: "MENA Sourcing — WEYB" },
        { fr: "Sourcing en Tunisie, Égypte, Maroc et Jordanie : WEYB connecte acheteurs et fournisseurs qualifiés dans la zone MENA.", en: "Sourcing in Tunisia, Egypt, Morocco and Jordan: WEYB connects buyers with qualified MENA suppliers." }
    );
    const { lang, prefix } = useLang();
    const C = COPY[lang];
    const [form, setForm] = useState(initialForm);
    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const toggle = (key, value) =>
        setForm((f) => ({
            ...f,
            [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
        }));

    const stepError = (s) => {
        if (s === 1 && !form.userType) return C.err1;
        if (s === 2 && (!form.sector || form.countries.length === 0)) return C.err2;
        if (s === 3) {
            if (form.userType === "buyer" && (!form.budget || !form.timeline)) return C.err3;
            if (form.userType === "supplier" && !form.volume) return C.err3;
        }
        if (s === 5 && form.description.trim().length < 20) return C.err5;
        if (s === 6) {
            const ok =
                form.name.trim().length >= 3 &&
                form.company.trim().length >= 3 &&
                EMAIL_RE.test(form.email.trim()) &&
                form.phone.trim().length >= 9 &&
                form.country.trim().length > 0;
            if (!ok) return C.err6;
        }
        return null;
    };

    const next = () => {
        const err = stepError(step);
        if (err) {
            toast.error(err);
            return;
        }
        setStep((s) => Math.min(s + 1, TOTAL_STEPS));
    };

    const submit = async () => {
        for (let s = 1; s <= 6; s++) {
            const err = stepError(s);
            if (err) {
                toast.error(err);
                setStep(s);
                return;
            }
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/sourcing-lead`, {
                lang,
                userType: form.userType,
                sector: C.sectors[form.sector],
                countries: form.countries.map((c) => C.countries[c]),
                budget: form.budget,
                timeline: form.timeline,
                volume: form.volume,
                certifications: form.certifications,
                description: form.description,
                name: form.name,
                company: form.company,
                email: form.email,
                phone: form.phone,
                country: form.country,
            });
            trackEvent("form_submit", { form: "sourcing" });
            setDone(true);
            toast.success(C.successToast);
        } catch (e) {
            toast.error(e.response?.data?.detail || C.errGeneric);
        } finally {
            setSubmitting(false);
        }
    };

    const serviceLd = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "MENA Supplier Matching",
        description: "Connect buyers and suppliers in MENA region",
        areaServed: ["TN", "EG", "MA", "JO"],
        serviceType: ["B2B Sourcing", "Supplier Matching"],
    };

    return (
        <section data-testid="sourcing-page" className="bg-[#F9FAFB] pt-32 md:pt-40 pb-24 md:pb-32 min-h-screen">
            <script type="application/ld+json">{JSON.stringify(serviceLd)}</script>
            <div className="max-w-3xl mx-auto px-6">
                <div className="text-center mb-12">
                    <h1 className="font-display text-3xl md:text-5xl font-light tracking-tight text-weyb-ink leading-tight">
                        {C.heroTitle}
                    </h1>
                    <p className="mt-4 text-sm md:text-base text-gray-600 max-w-xl mx-auto leading-relaxed">
                        {C.heroSubtitle}
                    </p>
                </div>

                {done ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: EASE }}
                        data-testid="sourcing-success"
                        className="bg-white border border-gray-200 rounded-sm p-10 md:p-14 text-center"
                    >
                        <CheckCircle2 size={36} className="mx-auto text-weyb-yellow-dark" aria-hidden="true" />
                        <h2 className="mt-5 font-display text-2xl md:text-3xl font-medium text-weyb-ink">{C.successTitle}</h2>
                        <p className="mt-3 text-sm text-gray-600 max-w-md mx-auto">{C.successText}</p>
                        <div className="mt-8 flex flex-wrap justify-center gap-4">
                            <Link
                                to={prefix || "/"}
                                data-testid="sourcing-success-home"
                                className="inline-flex items-center gap-2 border border-gray-300 text-weyb-ink hover:border-weyb-yellow px-6 py-3 rounded-sm text-sm font-semibold transition-colors"
                            >
                                {C.backHome}
                            </Link>
                            <a
                                href={CAL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="sourcing-success-call"
                                className="inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-6 py-3 rounded-sm text-sm font-semibold transition-colors"
                            >
                                {C.bookCall}
                                <ArrowUpRight size={15} />
                            </a>
                        </div>
                    </motion.div>
                ) : (
                    <>
                        {/* Progression */}
                        <div className="mb-6" data-testid="sourcing-progress">
                            <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-2">
                                <span>{C.stepOf.replace("{x}", step)}</span>
                                <span>{Math.round((step / TOTAL_STEPS) * 100)}%</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-weyb-yellow transition-all duration-500"
                                    style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
                                />
                            </div>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-sm p-7 md:p-10">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={step}
                                    initial={{ opacity: 0, x: 24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -24 }}
                                    transition={{ duration: 0.3, ease: EASE }}
                                >
                                    <h2 className="font-display text-xl md:text-2xl font-medium text-weyb-ink mb-7">
                                        {C.steps[step - 1]}
                                    </h2>

                                    {step === 1 && (
                                        <div className="space-y-3" role="radiogroup" aria-label={C.steps[0]}>
                                            {[
                                                { v: "buyer", label: C.buyer },
                                                { v: "supplier", label: C.supplier },
                                            ].map((o) => (
                                                <button
                                                    key={o.v}
                                                    type="button"
                                                    role="radio"
                                                    aria-checked={form.userType === o.v}
                                                    data-testid={`sourcing-usertype-${o.v}`}
                                                    onClick={() => set("userType", o.v)}
                                                    className={`w-full text-left border rounded-sm px-5 py-4 text-sm font-medium transition-colors ${
                                                        form.userType === o.v
                                                            ? "border-weyb-yellow bg-weyb-yellow/10 text-weyb-ink"
                                                            : "border-gray-200 text-gray-600 hover:border-weyb-yellow"
                                                    }`}
                                                >
                                                    {o.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {step === 2 && (
                                        <div className="space-y-7">
                                            <div>
                                                <label htmlFor="sector" className={labelCls}>{C.sectorLabel}</label>
                                                <select
                                                    id="sector"
                                                    data-testid="sourcing-sector"
                                                    value={form.sector}
                                                    onChange={(e) => set("sector", e.target.value)}
                                                    className={selectCls}
                                                >
                                                    <option value="">{C.sectorPh}</option>
                                                    {Object.entries(C.sectors).map(([v, l]) => (
                                                        <option key={v} value={v}>{l}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <p className={labelCls}>{C.countriesLabel}</p>
                                                <p className="text-xs text-gray-400 mb-3">{C.countriesHelp}</p>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    {Object.entries(C.countries).map(([v, l]) => (
                                                        <label
                                                            key={v}
                                                            data-testid={`sourcing-country-${v}`}
                                                            className={`flex items-center gap-3 border rounded-sm px-4 py-3 text-sm cursor-pointer transition-colors ${
                                                                form.countries.includes(v)
                                                                    ? "border-weyb-yellow bg-weyb-yellow/10 text-weyb-ink"
                                                                    : "border-gray-200 text-gray-600 hover:border-weyb-yellow"
                                                            }`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className="accent-[#FBBC13]"
                                                                checked={form.countries.includes(v)}
                                                                onChange={() => toggle("countries", v)}
                                                            />
                                                            {l}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {step === 3 && form.userType === "buyer" && (
                                        <div className="space-y-6">
                                            <div>
                                                <label htmlFor="budget" className={labelCls}>{C.budgetLabel}</label>
                                                <select id="budget" data-testid="sourcing-budget" value={form.budget} onChange={(e) => set("budget", e.target.value)} className={selectCls}>
                                                    <option value="">{C.budgetPh}</option>
                                                    {C.budgets.map((b) => (
                                                        <option key={b} value={b}>{b}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label htmlFor="timeline" className={labelCls}>{C.timelineLabel}</label>
                                                <select id="timeline" data-testid="sourcing-timeline" value={form.timeline} onChange={(e) => set("timeline", e.target.value)} className={selectCls}>
                                                    <option value="">{C.timelinePh}</option>
                                                    {C.timelines.map((tl) => (
                                                        <option key={tl} value={tl}>{tl}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    {step === 3 && form.userType === "supplier" && (
                                        <div>
                                            <label htmlFor="volume" className={labelCls}>{C.volumeLabel}</label>
                                            <select id="volume" data-testid="sourcing-volume" value={form.volume} onChange={(e) => set("volume", e.target.value)} className={selectCls}>
                                                <option value="">{C.volumePh}</option>
                                                {C.volumes.map((v) => (
                                                    <option key={v} value={v}>{v}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {step === 4 && (
                                        <div>
                                            <p className={labelCls}>{C.certLabel}</p>
                                            <p className="text-xs text-gray-400 mb-4">{C.certHelp}</p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                                {(CERTS[form.sector] || []).map((cert) => (
                                                    <label
                                                        key={cert}
                                                        data-testid={`sourcing-cert-${cert.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                                        className={`flex items-center gap-3 border rounded-sm px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                                                            form.certifications.includes(cert)
                                                                ? "border-weyb-yellow bg-weyb-yellow/10 text-weyb-ink"
                                                                : "border-gray-200 text-gray-600 hover:border-weyb-yellow"
                                                        }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="accent-[#FBBC13]"
                                                            checked={form.certifications.includes(cert)}
                                                            onChange={() => toggle("certifications", cert)}
                                                        />
                                                        {cert}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {step === 5 && (
                                        <div>
                                            <label htmlFor="description" className={labelCls}>{C.descLabel}</label>
                                            <textarea
                                                id="description"
                                                data-testid="sourcing-description"
                                                rows={6}
                                                maxLength={1000}
                                                value={form.description}
                                                onChange={(e) => set("description", e.target.value.slice(0, 1000))}
                                                placeholder={C.descPh}
                                                className={inputCls}
                                            />
                                            <p className="text-right text-xs text-gray-400 mt-2">{form.description.length} / 1000</p>
                                        </div>
                                    )}

                                    {step === 6 && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                            <div>
                                                <label htmlFor="s-name" className={labelCls}>{C.lName} *</label>
                                                <input id="s-name" data-testid="sourcing-name" maxLength={100} value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
                                            </div>
                                            <div>
                                                <label htmlFor="s-company" className={labelCls}>{C.lCompany} *</label>
                                                <input id="s-company" data-testid="sourcing-company" maxLength={150} value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} />
                                            </div>
                                            <div>
                                                <label htmlFor="s-email" className={labelCls}>{C.lEmail} *</label>
                                                <input id="s-email" type="email" data-testid="sourcing-email" maxLength={255} value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
                                            </div>
                                            <div>
                                                <label htmlFor="s-phone" className={labelCls}>{C.lPhone} *</label>
                                                <input id="s-phone" type="tel" data-testid="sourcing-phone" maxLength={20} value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
                                            </div>
                                            <div className="sm:col-span-2">
                                                <label htmlFor="s-country" className={labelCls}>{C.lCountry} *</label>
                                                <input id="s-country" data-testid="sourcing-country-input" maxLength={80} value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} />
                                            </div>
                                        </div>
                                    )}

                                    {step === 7 && (
                                        <div>
                                            <p className="text-sm text-gray-500 mb-6">{C.s7sub}</p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <button
                                                    type="button"
                                                    onClick={submit}
                                                    disabled={submitting}
                                                    data-testid="sourcing-submit-quote"
                                                    className="text-left border-2 border-weyb-yellow rounded-sm p-6 hover:bg-weyb-yellow/10 transition-colors disabled:opacity-60"
                                                >
                                                    <FileText size={20} className="text-weyb-yellow-dark mb-3" aria-hidden="true" />
                                                    <span className="block font-display font-semibold text-weyb-ink">
                                                        {submitting ? (
                                                            <span className="inline-flex items-center gap-2">
                                                                <Loader2 size={15} className="animate-spin" />
                                                                {C.sending}
                                                            </span>
                                                        ) : (
                                                            C.quoteTitle
                                                        )}
                                                    </span>
                                                    <span className="block mt-2 text-xs text-gray-500 leading-relaxed">{C.quoteDesc}</span>
                                                </button>
                                                <a
                                                    href={CAL_URL}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid="sourcing-book-call"
                                                    className="text-left border border-gray-200 rounded-sm p-6 hover:border-weyb-yellow transition-colors"
                                                >
                                                    <PhoneCall size={20} className="text-weyb-yellow-dark mb-3" aria-hidden="true" />
                                                    <span className="block font-display font-semibold text-weyb-ink">{C.callTitle}</span>
                                                    <span className="block mt-2 text-xs text-gray-500 leading-relaxed">{C.callDesc}</span>
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            </AnimatePresence>

                            {/* Navigation */}
                            <div className="mt-10 pt-6 border-t border-gray-100 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={() => setStep((s) => Math.max(s - 1, 1))}
                                    disabled={step === 1 || submitting}
                                    data-testid="sourcing-back"
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-gray-400 hover:text-weyb-ink disabled:opacity-40 disabled:hover:text-gray-400 transition-colors"
                                >
                                    <ArrowLeft size={15} />
                                    {C.back}
                                </button>
                                {step < TOTAL_STEPS && (
                                    <button
                                        type="button"
                                        onClick={next}
                                        data-testid="sourcing-next"
                                        className="inline-flex items-center gap-2 bg-weyb-ink text-white hover:bg-black px-6 py-3 rounded-sm text-sm font-semibold transition-colors"
                                    >
                                        {C.next}
                                        <ArrowRight size={15} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
